import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { BookOpen, AlertTriangle, CheckCircle, Brain, Shield, Scale, HelpCircle } from "lucide-react";

const sections = [
  {
    title: "Was ist die MPU?",
    icon: HelpCircle,
    content: "Die Medizinisch-Psychologische Untersuchung (MPU) ist eine Begutachtung der Fahreignung. Sie wird von einer amtlich anerkannten Begutachtungsstelle durchgeführt und besteht aus einem medizinischen Teil, einem Leistungsteil (Reaktions- und Konzentrationstests) und dem psychologischen Gespräch."
  },
  {
    title: "Der psychologische Teil",
    icon: Brain,
    content: "Im psychologischen Gespräch geht es darum, dem Gutachter zu zeigen, dass Sie die Ursachen Ihres Fehlverhaltens verstanden haben, eine ehrliche Selbstreflexion stattgefunden hat, und Sie stabile Veränderungen in Ihrem Verhalten vorgenommen haben. Der Gutachter prüft, ob eine erneute Auffälligkeit unwahrscheinlich ist."
  },
  {
    title: "Häufige Fehler",
    icon: AlertTriangle,
    items: [
      "Bagatellisierung des Vorfalls ('War doch nicht so schlimm')",
      "Schuldzuweisung an andere ('Wenn mein Chef mich nicht so gestresst hätte...')",
      "Ausweichende Antworten ohne konkrete Details",
      "Unrealistische Versprechungen ('Ich trinke nie wieder')",
      "Fehlende Eigenverantwortung für das Verhalten",
      "Widersprüche in der eigenen Geschichte",
      "Zu wenig Selbstreflexion und Einsicht"
    ]
  },
  {
    title: "Was Gutachter erwarten",
    icon: CheckCircle,
    items: [
      "Ehrliche und detaillierte Darstellung der Vorgeschichte",
      "Klares Verständnis der eigenen Verhaltensmuster",
      "Nachvollziehbare Veränderungsmotivation",
      "Konkrete und realistische Veränderungsschritte",
      "Glaubwürdige Rückfallprävention",
      "Eigenverantwortung und Einsicht",
      "Stimmiges Gesamtbild ohne Widersprüche"
    ]
  },
  {
    title: "Rechtliche Hinweise",
    icon: Scale,
    content: "Dieses Portal ist ein Übungswerkzeug zur Vorbereitung auf das psychologische Gespräch der MPU. Es ersetzt keine professionelle MPU-Beratung, keine Therapie und keine Rechtsberatung. Die Nutzung dieses Portals garantiert nicht das Bestehen der MPU. Für eine umfassende Vorbereitung empfehlen wir zusätzlich die Begleitung durch einen qualifizierten MPU-Berater."
  }
];

const faqItems = [
  { q: "Wie lange dauert eine MPU?", a: "Eine MPU dauert in der Regel 2-4 Stunden, je nach Anlass. Das psychologische Gespräch selbst dauert ca. 30-60 Minuten." },
  { q: "Wie oft kann ich das Training nutzen?", a: "Sie können so oft trainieren, wie Sie möchten. Regelmäßiges Üben verbessert Ihre Sicherheit und Ihre Antworten." },
  { q: "Werden meine Daten gespeichert?", a: "Ja, Ihre Gespräche werden gespeichert, damit Sie Ihren Fortschritt verfolgen können. Sie können Ihre Daten jederzeit löschen lassen." },
  { q: "Ersetzt dieses Training eine professionelle Beratung?", a: "Nein. Unser Tool ist eine Ergänzung zur professionellen Vorbereitung. Wir empfehlen, zusätzlich einen qualifizierten MPU-Berater aufzusuchen." },
  { q: "Welche Erfolgsquote hat die MPU?", a: "Die durchschnittliche Bestehensquote liegt bei etwa 60%. Mit guter Vorbereitung und echten Veränderungen steigen die Chancen deutlich." },
];

export default function KnowledgePage() {
  return (
    <div className="page-transition space-y-8" data-testid="knowledge-page">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Wissensbereich</h1>
        <p className="font-body text-sm text-muted-foreground mt-1">Grundlagen und Tipps für Ihre MPU-Vorbereitung</p>
      </div>

      <div className="grid gap-6">
        {sections.map((section, i) => (
          <Card key={i} className="border-border/50 hover-lift" data-testid={`knowledge-section-${i}`}>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-primary" />
                </div>
                <CardTitle className="font-heading text-lg">{section.title}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {section.content && <p className="font-body text-sm text-muted-foreground leading-relaxed">{section.content}</p>}
              {section.items && (
                <ul className="space-y-2">
                  {section.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                      <span className="font-body text-sm text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* FAQ */}
      <Card className="border-border/50" data-testid="faq-section">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle className="font-heading text-lg">Häufig gestellte Fragen</CardTitle>
              <CardDescription className="font-body text-sm">Antworten auf die wichtigsten Fragen</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="font-body text-sm font-medium text-left" data-testid={`faq-trigger-${i}`}>{item.q}</AccordionTrigger>
                <AccordionContent className="font-body text-sm text-muted-foreground">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <div className="bg-muted/50 rounded-2xl p-6 border border-border/50">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-body text-sm font-medium mb-1">Wichtiger Hinweis</p>
            <p className="font-body text-xs text-muted-foreground leading-relaxed">
              Alle Informationen auf dieser Seite dienen ausschließlich der allgemeinen Information und Vorbereitung.
              Sie stellen keine Rechtsberatung, medizinische Beratung oder therapeutische Beratung dar. 
              Für eine individuelle Beratung wenden Sie sich bitte an einen qualifizierten Fachmann.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
